import { useMediaQuery, useTheme } from "@mui/material"
import { observer } from "mobx-react"
import { useCallback, useEffect } from "react"
import { useLocation } from "react-router"

import { MixpanelProperties } from "src/analytics/constants/properties"
import { getLocationName } from "src/analytics/helpers/getLocationName"
import { trackEvent } from "src/analytics/helpers/mixpanel_tracking"
import { avy_api_pkg_segment_SegmentType } from "src/api"
import { CenteredSpinner } from "src/components/CenteredSpinner"
import { SegmentPickerStore } from "src/components/SegmentPicker/store"
import { ISegmentItemListPrefixType } from "src/components/SegmentPicker/components/SegmentItemListPrefix/types"
import { GlobalStore } from "src/store"
import { StoreProvider } from "src/store/lib/StoreProvider"
import { useStore } from "src/store/lib/useStore"
import { TabbedSegmentPicker } from "src/components/SegmentPicker/TabbedSegmentPicker/index"
import { SplitSegmentPicker } from "src/components/SegmentPicker/SplitSegmentPicker/index"

type ISegmentPickerModalProps = {
    initialSegmentIds: number[]
    accessGroupId: number | null
    allowedSegmentTypes?: ISegmentForSearch["type"][]
    publishingLevel?: avy_api_pkg_segment_SegmentType
    segmentListItemPrefixType?: ISegmentItemListPrefixType
    onChange: (segmentIds: number[]) => void
    onClose: () => void
    onPublishGroupChange?: (publishGroupIds: number[]) => void
    dimensionIds?: number[]
}

const SegmentPickerModalImpl = observer((props: ISegmentPickerModalProps) => {
    const store = useStore(SegmentPickerStore)
    const gstore = useStore(GlobalStore)

    const theme = useTheme()
    const smallScreen = useMediaQuery(theme.breakpoints.down("md"))
    const location = useLocation()

    useEffect(() => {
        ;(async () => {
            await store.init({
                accessGroupId: props.accessGroupId,
                initialSegmentIds: props.initialSegmentIds,
                allowedSegmentTypes: props.allowedSegmentTypes,
                publishingLevel: props.publishingLevel,
                segmentListItemPrefixType:
                    props.segmentListItemPrefixType ?? "checkbox",
                dimensionIds: props.dimensionIds,
            })
        })()
        // NOTE: This empty values are to prevent the modal from being re-initialized when the modal is closed
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const onSave = useCallback(() => {
        props.onChange(Array.from(store.selectedSegmentIds))
        if (props.onPublishGroupChange != null) {
            props.onPublishGroupChange(
                Array.from(
                    store.selectedSegmentsDimensions,
                    (group) => group.id,
                ),
            )
        }
        trackEvent(
            "General | Save Segment Picker Selection",
            getLocationName(location.pathname),
            {
                [MixpanelProperties.AccessGroupID]:
                    gstore.session.accessGroup?.id,
                [MixpanelProperties.AccessGroupName]:
                    gstore.session.accessGroup?.name,
                [MixpanelProperties.UserID]: gstore.session.user?.adminId,
                [MixpanelProperties.SegmentName]:
                    store.selectedSegments.sourceItems.map((item) => ({
                        name: item.name,
                        type: item.type,
                    })),
                [MixpanelProperties.SegmentID]: Array.from(
                    store.selectedSegmentIds,
                ),
            },
        )
    }, [
        store,
        location,
        props,
        gstore.session.accessGroup,
        gstore.session.user,
    ])

    if (gstore.loading.is(SegmentPickerStore.LoadingKeys.init)) {
        return <CenteredSpinner />
    }

    if (smallScreen) {
        return (
            <TabbedSegmentPicker
                onSave={onSave}
                onClose={props.onClose}
                initialSegmentIds={props.initialSegmentIds}
            />
        )
    } else {
        return (
            <SplitSegmentPicker
                onSave={onSave}
                onClose={props.onClose}
                initialSegmentIds={props.initialSegmentIds}
            />
        )
    }
})

export const SegmentPickerModal = observer(
    (props: ISegmentPickerModalProps) => {
        return (
            <StoreProvider Store={SegmentPickerStore}>
                <SegmentPickerModalImpl {...props} />
            </StoreProvider>
        )
    },
)
